import React, { useEffect } from 'react';
import '../assets/style/profile-pdf.scss';
import { Divider } from '@blueprintjs/core';
import { useParams } from 'react-router-dom';
import Logo from '../assets/images/admin-logo.png';
import ApplicantDetailsServices from '../services/Admin/ApplicantDetailsServices';
import { AppToaster, convertObjectKeysFromSnakeToCamelCase, getDoctorInitials } from '../utils';
import useWindowSize from '../hooks/useWindowSize';
import { IApplicantData } from '../interfaces/applicantDetails';
import Loader from './Loader';
import ProfilePicture from './ProfilePicture';
import SectionLoader from './SectionLoader';

const ProfilePdf = () => {
  const params = useParams();
  const windowSize = useWindowSize();
  const [applicantDetails, setApplicantDetails] = React.useState<IApplicantData>();
  const [isLoading, setIsLoading] = React.useState<boolean>(true);

  const fetchData = async () => {
    ApplicantDetailsServices.getApplicantDetails(params.id!)
      .then((response: any) => {
        if (response.status === 200 && response.data.status === 200) {
          setApplicantDetails(convertObjectKeysFromSnakeToCamelCase(response.data.data));
        } else {
          AppToaster(windowSize.width).show({
            message: response?.data?.error?.ui_message,
            intent: 'danger',
          });
        }
        setIsLoading(false);
      })
      .catch((error) => {
        AppToaster(windowSize.width).show({
          message: error?.response?.data?.message,
          intent: 'danger',
        });
        setIsLoading(false);
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fullName = `${applicantDetails?.personalDetails.firstName} ${
    applicantDetails?.personalDetails.middleName ?? ''
  } ${applicantDetails?.personalDetails.lastName}`;

  const getSocietyAcronym = (value: string) => {
    const _value = value.split('[')[1];
    return _value?.substring(0, _value.indexOf(']'));
  };

  const getDegrees = () => {
    const data = applicantDetails?.professionalDetails.educationDetails.map((data, index) => (
      <span>
        {index + 1 === applicantDetails?.professionalDetails?.educationDetails.length
          ? `${data.degree}  `
          : `${data.degree}, `}
      </span>
    ));
    return data;
  };

  const getSocieties = () => {
    const data = applicantDetails?.professionalDetails.otherMemberships.map((data, index) => (
      <span>
        {index + 1 === applicantDetails?.professionalDetails.otherMemberships.length
          ? getSocietyAcronym(data.society)
          : `${getSocietyAcronym(data.society)}, `}
      </span>
    ));
    return data;
  };

  return (
    <div className='profie-pdf-container'>
      {isLoading ? (
        <SectionLoader className='section-loader-height' />
      ) : (
        <>
          <div className='row pdf-header'>
            <img src={Logo} width={30} height={30} alt='Website Logo' />
            <div className='title'>
              Ophthalmological Society Of West Bengal - Membership Portal
            </div>
          </div>
          <section className='section section-1'>
            <div className='col'>
              <div className='row'>
                <div className='col'>
                  <div className='row'>
                    <ProfilePicture
                      size={50}
                      imageURl={
                        applicantDetails?.personalDetails.profilePicture?.fileUrl ?? ''
                      }
                      name={fullName}
                      className='pdf-profile'
                    />
                    <div>
                      <div className='name'>
                        {getDoctorInitials(fullName)}
                        {fullName}
                      </div>
                      <div className='degree-council'>
                        {getDegrees()}
                        <div>{getSocieties()}</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className='col'>
                  <div className='membership-no'>
                    Membership No:
                    {' '}
                    {applicantDetails?.membershipDetails.membershipId ?? '-'}
                  </div>
                </div>
              </div>
              <div className='row'>
                <div className='col'>
                  <div className='title'>Bio</div>
                  <div className='content'>{applicantDetails?.personalDetails.aboutMe}</div>
                </div>
              </div>
            </div>
          </section>
          <section className='section section-2'>
            <div className='row section-header'>Personal Details</div>
            <div className='row'>
              <div className='col'>
                <div className='row'>
                  <div className='col title'>Full Name: </div>
                  <div className='col value name'>{fullName}</div>
                </div>
                <div className='row'>
                  <div className='col title'>Date of Birth: </div>
                  <div className='col value'>
                    {new Date(applicantDetails?.personalDetails.dob!).toLocaleDateString()}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Sex: </div>
                  <div className='col value'>{applicantDetails?.personalDetails.sex}</div>
                </div>
                <div className='row'>
                  <div className='col title'>Current Address: </div>
                  <div className='col value'>
                    <div>
                      <div>{applicantDetails?.contactDetails.currentAddress.addressLine1}</div>
                      <div>{applicantDetails?.contactDetails.currentAddress.addressLine2}</div>
                      <div>{applicantDetails?.contactDetails.currentAddress.city}</div>
                      <div>{applicantDetails?.contactDetails.currentAddress.state}</div>
                      <div>{applicantDetails?.contactDetails.currentAddress.pincode}</div>
                      <div>{applicantDetails?.contactDetails.currentAddress.country}</div>
                    </div>
                  </div>
                </div>
              </div>
              <Divider />
              <div className='col'>
                <div className='row'>
                  <div className='col title'>Email: </div>
                  <div className='col value email-text'>{applicantDetails?.personalDetails.email}</div>
                </div>
                <div className='row'>
                  <div className='col title'>Mobile Number: </div>
                  <div className='col value'>{`${applicantDetails?.contactDetails.countryCode} - ${applicantDetails?.contactDetails.mobileNumber}`}</div>
                </div>
                <div className='row'>
                  <div className='col title'>WhatApp Number: </div>
                  <div className='col value'>
                    {applicantDetails?.contactDetails.isWhatsapp ? 'Yes' : 'No'}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Permanent Address: </div>
                  <div className='col value'>
                    <div>
                      <div>
                        {applicantDetails?.contactDetails.permanentAddress.addressLine1}
                      </div>
                      <div>
                        {applicantDetails?.contactDetails.permanentAddress.addressLine2}
                      </div>
                      <div>{applicantDetails?.contactDetails.permanentAddress.city}</div>
                      <div>{applicantDetails?.contactDetails.permanentAddress.state}</div>
                      <div>{applicantDetails?.contactDetails.permanentAddress.pincode}</div>
                      <div>{applicantDetails?.contactDetails.permanentAddress.country}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className='section section-3'>
            <div className='row section-header'>Professional Details</div>
            <div className='row'>
              <div className='col'>
                <div className='row'>
                  <div className='col title'>Specialisation: </div>
                  <div className='col value'>
                    {applicantDetails?.professionalDetails.specialisations.map(
                      (data, index) => (
                        <div>{data.name}</div>
                      ),
                    )}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Education Details: </div>
                  <div className='col value'>
                    {applicantDetails?.professionalDetails.educationDetails.map(
                      (data, index) => (
                        <div>
                          <div>{data.degree}</div>
                          <div>{data.specialisation}</div>
                          <div>{data.institution}</div>
                          {data.isOngoing ? (
                            <i>( Currently Pursuing )</i>
                          ) : (
                            <i>
                              ( awarded in
                              {data.year}
                              {' '}
                              )
                            </i>
                          )}
                        </div>
                      ),
                    )}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Medical Council: </div>
                  <div className='col value'>
                    <div>
                      {applicantDetails?.professionalDetails.medicalRegistration.council}
                      <div>
                        {applicantDetails?.professionalDetails.medicalRegistration.number}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className='col'>
                <div className='row'>
                  <div className='col title'>Other Professional Memberships: </div>
                  <div className='col value'>
                    {applicantDetails?.professionalDetails.otherMemberships.map(
                      (data, index) => (
                        <div>
                          <div>{data.society}</div>
                          <div>{data.membershipNumber}</div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Work Experience: </div>
                  <div className='col value'>
                    {applicantDetails?.professionalDetails.workExperiences.map(
                      (data, index) => (
                        <div>
                          <div>{data.institution}</div>
                          <div>{data.designation}</div>
                          <div>
                            {new Date(data.start).toLocaleDateString()}
                            {' '}
                            -
                            {' '}
                            {data.isCurrent
                              ? 'Current'
                              : new Date(data.end!).toLocaleDateString()}
                          </div>
                          <i>
                            Is Public Sector:
                            {data.isPublicSector ? 'YES' : 'NO'}
                          </i>
                          <div>
                            <i>
                              Is Teaching Involves:
                              {data.isTeaching ? 'YES' : 'NO'}
                            </i>
                          </div>
                        </div>
                      ),
                    )}
                  </div>
                </div>
              </div>
            </div>
          </section>
          <section className='section section-4'>
            <div className='row section-header'>Membership Details</div>
            <div className='row'>
              <div className='col'>
                <div className='row'>
                  <div className='col title'>Status: </div>
                  <div className='col value'>
                    {applicantDetails?.membershipDetails.status ?? '-'}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Membership No: </div>
                  <div className='col value'>
                    {applicantDetails?.membershipDetails.membershipId ?? '-'}
                  </div>
                </div>
              </div>
              <Divider />
              <div className='col'>
                <div className='row'>
                  <div className='col title'>Member Since: </div>
                  <div className='col value'>
                    {applicantDetails?.membershipDetails.createdOn
                      ? new Date(
                        applicantDetails?.membershipDetails.createdOn,
                      ).toLocaleDateString()
                      : '-'}
                  </div>
                </div>
                <div className='row'>
                  <div className='col title'>Membership Plan: </div>
                  <div className='col value'>
                    {applicantDetails?.membershipDetails.membershipPlan ?? '-'}
                  </div>
                </div>
              </div>
            </div>
          </section>
        </>
      )}
    </div>
  );
};

export default ProfilePdf;
